import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Lightbulb,
  Search,
  Unlock,
  Lock as LockIcon,
  Info,
  Calendar,
  ChevronRight,
  ChevronUp,
  Lock,
  UserPlus,
  UserMinus,
  User,
  X,
  Filter,
  ChevronDown,
  BookOpen,
  XCircle,
  Users,
  MapPin,
} from 'lucide-react';

import Button from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import ReferenceLookupModal from '../components/ReferenceLookupModal';
import api from '../api/axios';
import { createTicket, getNextTicketNumber } from '../api/tickets';
import { uploadAttachment } from '../api/attachments';
import { getUsers } from '../api/users';
import { addFollower } from '../api/followers';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { cleanUserName } from '../lib/utils';

const PREDEFINED_REGIONS = ["Pune", "Gurugram", "Delhi NCR", "Odisha", "NCR"];

// Tooltip component
const Tooltip = ({ children, content, position = 'top' }) => {
  const [show, setShow] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShow(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setShow(false), 100);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  };

  return (
    <div
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {show && content && (
        <div
          className={`absolute z-50 px-2 py-1 text-xs text-white bg-slate-900 rounded shadow-lg whitespace-nowrap ${positionClasses[position]}`}
        >
          {content}
          <div className={`absolute ${position === 'top' ? 'top-full' : 'bottom-full'} left-1/2 -translate-x-1/2 border-4 border-transparent ${position === 'top' ? 'border-t-slate-900' : 'border-b-slate-900'}`} />
        </div>
      )}
    </div>
  );
};

// Knowledge Base Suggestions Modal
const KBSuggestionsModal = ({ isOpen, onClose, searchTerm, onSelectSuggestion }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!isOpen || !searchTerm || searchTerm.trim().length < 3) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Search KB articles based on the search term
        const response = await api.get('/knowledge/search', {
          params: {
            query: searchTerm.trim(),
            limit: 10
          }
        });

        if (response.data?.success) {
          const articles = response.data.data || [];
          
          // Calculate relevance score based on keyword matches
          const articlesWithRelevance = articles.map(article => {
            const searchLower = searchTerm.toLowerCase();
            const titleLower = (article.title || '').toLowerCase();
            const contentLower = (article.content || '').toLowerCase();
            
            // Calculate relevance: title match = 60%, content match = 40%
            let relevance = 0;
            
            // Title relevance
            if (titleLower.includes(searchLower)) {
              relevance += 60;
            } else {
              // Partial word matches in title
              const searchWords = searchLower.split(' ').filter(w => w.length > 2);
              const titleMatches = searchWords.filter(word => titleLower.includes(word)).length;
              relevance += (titleMatches / Math.max(searchWords.length, 1)) * 60;
            }
            
            // Content relevance
            if (contentLower.includes(searchLower)) {
              relevance += 40;
            } else {
              // Partial word matches in content
              const searchWords = searchLower.split(' ').filter(w => w.length > 2);
              const contentMatches = searchWords.filter(word => contentLower.includes(word)).length;
              relevance += (contentMatches / Math.max(searchWords.length, 1)) * 40;
            }
            
            return {
              ...article,
              relevance: Math.min(Math.round(relevance), 100)
            };
          });
          
          // Sort by relevance and filter out low relevance results
          const sortedArticles = articlesWithRelevance
            .filter(article => article.relevance > 20)
            .sort((a, b) => b.relevance - a.relevance);
          
          setSuggestions(sortedArticles);
        } else {
          setSuggestions([]);
        }
      } catch (err) {
        console.error('KB search error:', err);
        // If KB API doesn't exist yet, show helpful message
        if (err.response?.status === 404) {
          setError('Knowledge Base is not available yet. Please contact your administrator.');
        } else {
          setError('Failed to search Knowledge Base. Please try again.');
        }
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [isOpen, searchTerm]);

  const handleSelectArticle = (article) => {
    if (onSelectSuggestion) {
      onSelectSuggestion(article);
    }
    // You could also open the article in a new tab or show full content
    toast.success(`Selected: ${article.title}`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-hidden border border-gray-200 dark:border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-lg">
              <Lightbulb className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Knowledge Base Suggestions</h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                {searchTerm ? `Searching for: "${searchTerm}"` : 'Enter a description to get suggestions'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <XCircle className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto max-h-[calc(85vh-120px)]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Searching Knowledge Base...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">{error}</p>
            </div>
          ) : suggestions.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Found <span className="font-semibold text-slate-900 dark:text-slate-100">{suggestions.length}</span> relevant article{suggestions.length !== 1 ? 's' : ''}
              </p>
              {suggestions.map((article) => (
                <div
                  key={article.id}
                  onClick={() => handleSelectArticle(article)}
                  className="group p-4 border border-slate-200 dark:border-slate-700 rounded-xl hover:border-amber-300 dark:hover:border-amber-600 hover:bg-amber-50/50 dark:hover:bg-amber-900/10 cursor-pointer transition-all duration-200 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        <h4 className="font-semibold text-slate-900 dark:text-slate-100 group-hover:text-amber-700 dark:group-hover:text-amber-300 transition-colors line-clamp-2">
                          {article.title}
                        </h4>
                      </div>
                      {article.content && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-2">
                          {article.content.substring(0, 150)}...
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full font-medium ${
                          article.relevance >= 80 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : article.relevance >= 60
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                            : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'
                        }`}>
                          {article.relevance}% match
                        </span>
                        {article.category && (
                          <span className="text-slate-500 dark:text-slate-400">
                            {article.category}
                          </span>
                        )}
                        {article.views !== undefined && (
                          <span className="text-slate-500 dark:text-slate-400">
                            {article.views} views
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors flex-shrink-0 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-slate-400 dark:text-slate-500" />
              </div>
              <p className="text-slate-600 dark:text-slate-400 font-medium mb-2">
                {searchTerm && searchTerm.trim().length >= 3 
                  ? `No articles found for "${searchTerm}"`
                  : 'Enter at least 3 characters to search'}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-500">
                Try different keywords or check the spelling
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {suggestions.length > 0 && (
          <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
              Click on an article to view more details or apply the solution
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Reference field with icon buttons
const ReferenceField = ({
  value,
  onChange,
  placeholder = 'Search...',
  items = [],
  displayKey = 'name',
  disabled = false,
  readOnly = false, // Input read-only (e.g. customer Caller); search icon still works
  showLock = false,
  showInfo = true,
  showSearch = true,
  showCalendar = false,
  infoTooltip = '',
  fieldName = '',
  showDetails = false, // Show description, email, member count
  searchFields = [], // Additional fields to search (e.g., ['description', 'email'])
  onSearchClick = null, // Callback to open modal instead of dropdown
}) => {
  const selected = useMemo(() => {
    if (!value || !items.length) return null;
    // Ensure proper type conversion for comparison
    const valueStr = value.toString().trim();
    if (!valueStr || valueStr === 'undefined' || valueStr === 'null' || valueStr === 'admin0') return null;
    return items.find(i => {
      const itemId = i.id?.toString().trim();
      return itemId && itemId === valueStr;
    }) || null;
  }, [items, value]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isLocked, setIsLocked] = useState(false);

  const filtered = useMemo(() => {
    if (!query) {
      const result = items.slice(0, 6);
      return result;
    }
    const queryLower = query.toLowerCase();
    const result = items
      .filter(i => {
        // Search in displayKey (usually 'name')
        const mainMatch = (i[displayKey] || '').toLowerCase().includes(queryLower);
        // Search in additional fields if provided
        const additionalMatch = searchFields.length > 0 && searchFields.some(field => 
          (i[field] || '').toLowerCase().includes(queryLower)
        );
        return mainMatch || additionalMatch;
      })
      .slice(0, 6);
    return result;
  }, [items, query, displayKey, searchFields]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isInsideDropdown = target.closest('.reference-dropdown');
      const isInsideField = target.closest('.reference-field');
      const isSearchInput = target.closest('.reference-dropdown input[type="text"]');
      
      if (open && !isInsideDropdown && !isInsideField && !isSearchInput) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const displayValue = useMemo(() => {
    if (selected && selected[displayKey]) {
      return selected[displayKey];
    }
    // If value exists but no matching item found, return empty string to show placeholder
    // This prevents showing invalid values like "admin0" or raw IDs
    return '';
  }, [selected, displayKey]);

  const handleLockToggle = () => {
    setIsLocked(!isLocked);
    toast.info(isLocked ? `${fieldName || 'Field'} unlocked` : `${fieldName || 'Field'} locked`);
  };

  return (
    <div className="relative reference-field">
      <input
        type="text"
        value={displayValue}
        readOnly
        onClick={() => {
          if (!disabled && !isLocked && !readOnly) {
            if (process.env.NODE_ENV === 'development') {
              console.log('Opening dropdown, items:', items.length);
            }
            setOpen(true);
          }
        }}
        placeholder={placeholder}
        disabled={disabled || isLocked}
        className={`w-full border border-gray-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 pr-20 px-4 py-2.5 text-sm transition-all duration-200 ${isLocked ? 'bg-gray-100/80 dark:bg-slate-800/60 cursor-not-allowed opacity-75' : ''
          } ${readOnly ? 'bg-gray-50 dark:bg-slate-800/80 cursor-default' : ''} ${!disabled && !isLocked && !readOnly ? 'cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500' : ''}`}
      />
      <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
        {showSearch && (
          <Tooltip content="Search and select">
            <button
              type="button"
              className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled && !isLocked) {
                  if (onSearchClick) {
                    onSearchClick();
                  } else if (!readOnly) {
                    setOpen(true);
                  }
                }
              }}
              disabled={disabled || isLocked}
            >
              <Search className="w-4 h-4" />
            </button>
          </Tooltip>
        )}
        {showLock && (
          <Tooltip content={isLocked ? 'Unlock field' : 'Lock field'}>
            <button
              type="button"
              className={`p-2 rounded-xl border border-gray-200 dark:border-slate-700 transition-all duration-200 ${isLocked
                  ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700'
                  : 'bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 text-gray-500 dark:text-gray-400'
                }`}
              onClick={handleLockToggle}
            >
              {isLocked ? <LockIcon className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
            </button>
          </Tooltip>
        )}
        {showInfo && (
          <Tooltip content={infoTooltip || `Information about ${fieldName || 'this field'}`}>
            <button
              type="button"
              className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200"
            >
              <Info className="w-4 h-4" />
            </button>
          </Tooltip>
        )}
        {showCalendar && (
          <Tooltip content="Select date">
            <button
              type="button"
              className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200"
            >
              <Calendar className="w-4 h-4" />
            </button>
          </Tooltip>
        )}
      </div>
      {open && !disabled && !isLocked && (
        <div className="reference-dropdown absolute left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="p-2 border-b border-gray-200 dark:border-slate-700">
            <input
              type="text"
              className="w-full border border-gray-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all duration-200"
              placeholder="Type to search…"
              value={query}
              onChange={e => {
                e.stopPropagation();
                e.preventDefault();
                setQuery(e.target.value);
              }}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onFocus={e => {
                e.stopPropagation();
              }}
              onMouseDown={e => {
                e.stopPropagation();
              }}
              autoFocus
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-3 text-sm text-slate-500 dark:text-slate-400">
                No assignment groups available
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-3 text-sm text-slate-500 dark:text-slate-400">
                No results found for "{query}"
              </div>
            ) : (
              filtered.map(item => {
                const memberCount = item.member_count !== undefined && item.member_count !== null 
                  ? (typeof item.member_count === 'string' ? parseInt(item.member_count, 10) : item.member_count)
                  : null;
                const hasDetails = showDetails && (item.description || item.email || (memberCount !== null && memberCount !== undefined));
                
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-slate-700 text-sm transition-all duration-200 rounded mx-1"
                    onClick={() => {
                      onChange(item.id.toString());
                      setOpen(false);
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-gray-900 dark:text-gray-100 block">
                          {item[displayKey] || 'Unnamed Group'}
                        </span>
                        {hasDetails && (
                          <div className="mt-1 space-y-0.5">
                            {item.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                                {item.description}
                              </p>
                            )}
                            {(item.email || (memberCount !== null && memberCount !== undefined)) && (
                              <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 flex-wrap">
                                {item.email && (
                                  <span className="truncate">{item.email}</span>
                                )}
                                {memberCount !== null && memberCount !== undefined && (
                                  <span className="flex items-center gap-1 whitespace-nowrap">
                                    <Users className="w-3 h-3 flex-shrink-0" />
                                    {memberCount} {memberCount === 1 ? 'member' : 'members'}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function CreateTicket() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignableAgents, setAssignableAgents] = useState([]);
  const [assignmentGroups, setAssignmentGroups] = useState([]);
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [activityExpanded, setActivityExpanded] = useState(true);
  const [showActivitySample, setShowActivitySample] = useState(true);
  const [kbModalOpen, setKbModalOpen] = useState(false);
  const [watchListLocked, setWatchListLocked] = useState(false);
  const [watchListCreate, setWatchListCreate] = useState([]); // Users to add to watch list when ticket is created
  const [showAddWatchListModal, setShowAddWatchListModal] = useState(false);
  const [watchListModalSearch, setWatchListModalSearch] = useState('');
  /** Tenant admins merged into watch picker (with general user list) */
  const [tenantAdminsWatchUsers, setTenantAdminsWatchUsers] = useState([]);
  const [ticketNumber, setTicketNumber] = useState('');
  const [loadingNumber, setLoadingNumber] = useState(true);
  const [showCustomSubcategory, setShowCustomSubcategory] = useState(false);
  const [customSubcategoryName, setCustomSubcategoryName] = useState('');
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [lookupModal, setLookupModal] = useState({ isOpen: false, field: null, data: [], title: '', columns: [], displayKey: 'name' });
  
  const openedAt = new Date();

  const [form, setForm] = useState({
    subject: '',
    description: '',
    category_id: '',
    subcategory: '',
    location: '',
    contact_type: 'phone',
    // Internal incident state (UI only) – mapped to real DB statuses on submit
    state: 'new',
    impact: '3',
    urgency: '3',
    priority: '3',
    assignment_group: '',
    assigned_to: '',
    requester_id: '',
    additional_comments: '',
    work_notes: '',
  });

  // Location suggestions (only predefined regions)
  const locationSuggestions = useMemo(() => {
    const query = String(form.location || '').toLowerCase().trim();
    const unique = Array.from(new Set(PREDEFINED_REGIONS));
    if (!query) return unique;
    return unique
      .filter((item) => item.toLowerCase().includes(query))
      .sort((a, b) => a.length - b.length)
      .slice(0, 20);
  }, [form.location]);

  useEffect(() => {
    fetchCategories();
    fetchUsers();
    fetchAssignmentGroups();
    if (user) {
      setForm(f => ({ ...f, requester_id: user.id?.toString() || '' }));
    }
    // Load template if provided
    if (location.state?.template) {
      const template = location.state.template;
      setForm(f => ({
        ...f,
        subject: template.subject || '',
        description: template.description || '',
        category_id: template.category_id?.toString() || '',
        priority: template.priority?.toString() || '3',
      }));
      if (template.category_id) {
        fetchSubcategories(parseInt(template.category_id));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.state]);

  // Separate effect to fetch ticket number preview - runs after form.requester_id is set
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 2;

    const fetchNextTicketNumber = async (isRetry = false) => {
      // Priority: form.requester_id (for admin creating ticket for customer) > user.id (current user)
      const requesterId = form.requester_id || user?.id;
      
      if (!requesterId) {
        if (isMounted) {
          setLoadingNumber(false);
        }
        return;
      }

      try {
        if (isMounted) {
          setLoadingNumber(true);
        }
        console.log('[CreateTicket] Fetching next ticket number preview for requester:', requesterId, isRetry ? `(Retry ${retryCount})` : '');
        
        const response = await getNextTicketNumber(requesterId);
        console.log('[CreateTicket] Full response received:', JSON.stringify(response, null, 2));
        
        if (!isMounted) return; // Component unmounted, don't update state
        
        // Handle different response structures
        let ticketNumberValue = null;
        
        if (response) {
          // Check if response has success and data structure (standard format)
          if (response.success && response.data) {
            ticketNumberValue = response.data.ticket_number || 
                              response.data.ticketNumber || 
                              response.data.newId;
          } 
          // Check if response is the data directly (fallback)
          else if (response.ticket_number || response.ticketNumber || response.newId) {
            ticketNumberValue = response.ticket_number || response.ticketNumber || response.newId;
          }
          // Check if response.data exists without success flag (another fallback)
          else if (response.data) {
            ticketNumberValue = response.data.ticket_number || 
                              response.data.ticketNumber || 
                              response.data.newId;
          }
        }
        
        console.log('[CreateTicket] Extracted ticket number value:', ticketNumberValue);
        
        if (ticketNumberValue && (typeof ticketNumberValue === 'string' || typeof ticketNumberValue === 'number')) {
          let numberStr = String(ticketNumberValue).trim();
          
          // Extract only numeric part from formats like "TKT_1001_2026" or "TKT-1001-2026"
          // Look for the main numeric sequence (usually the middle number)
          const numericMatch = numberStr.match(/\d+/);
          if (numericMatch) {
            // Use the first significant numeric sequence found
            numberStr = numericMatch[0];
          }
          
          if (numberStr) {
            setTicketNumber(numberStr);
            console.log('[CreateTicket] ✅ Ticket number preview set:', numberStr);
          } else {
            console.warn('[CreateTicket] ⚠️ Ticket number is empty string after trim');
            if (isMounted) {
              setTicketNumber('');
            }
          }
        } else {
          console.warn('[CreateTicket] ⚠️ No valid ticket number found in response. Response structure:', response);
          // Retry if we haven't exceeded max retries
          if (retryCount < maxRetries && isMounted) {
            retryCount++;
            console.log(`[CreateTicket] Retrying... (${retryCount}/${maxRetries})`);
            setTimeout(() => {
              if (isMounted) {
                fetchNextTicketNumber(true);
              }
            }, 1000);
            return;
          }
          if (isMounted) {
            setTicketNumber('');
          }
        }
      } catch (error) {
        console.error('[CreateTicket] ❌ Failed to fetch next ticket number preview:', error);
        console.error('[CreateTicket] Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          stack: error.stack
        });
        
        if (!isMounted) return;
        
        // Retry on network errors or 5xx errors
        if (retryCount < maxRetries && (!error.response || error.response.status >= 500)) {
          retryCount++;
          console.log(`[CreateTicket] Retrying after error... (${retryCount}/${maxRetries})`);
          setTimeout(() => {
            if (isMounted) {
              fetchNextTicketNumber(true);
            }
          }, 1000);
          return;
        }
        
        // Show error toast but don't block the form (only on final failure)
        if (retryCount >= maxRetries) {
          toast.error('Could not load ticket number preview. It will be generated on submit.');
        }
        setTicketNumber(''); // Fallback: empty if API fails
      } finally {
        if (isMounted) {
          setLoadingNumber(false);
        }
      }
    };

    // Only fetch if we have a valid requester ID and user is authenticated
    if (user?.id) {
      // Small delay to ensure form.requester_id is set if it's being set in another useEffect
      const timer = setTimeout(() => {
        if (isMounted) {
          fetchNextTicketNumber();
        }
      }, 200);
      
      return () => {
        isMounted = false;
        clearTimeout(timer);
      };
    } else {
      setLoadingNumber(false);
    }
  }, [user?.id, form.requester_id]); // Re-fetch if user ID or requester changes

  useEffect(() => {
    const p = Math.round((Number(form.impact) + Number(form.urgency)) / 2);
    setForm(f => ({ ...f, priority: p.toString() }));
  }, [form.impact, form.urgency]);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      if (res.data?.success) {
        const allCategories = res.data.data || [];
        // Filter to only show "Technical Support" or find it
        const techSupport = allCategories.find(c =>
          c.name.toLowerCase().includes('technical') ||
          c.name.toLowerCase().includes('support')
        );

        if (techSupport) {
          setCategories([techSupport]);
          // Auto-select Technical Support category
          setForm(f => ({ ...f, category_id: techSupport.id.toString() }));
          // Fetch subcategories for this category
          fetchSubcategories(techSupport.id);
        } else {
          setCategories(allCategories);
        }
      }
    } catch (err) {
      // ignore
    }
  };

  const fetchSubcategories = async (categoryId) => {
    // Default electronic device subcategories (fallback)
    const deviceSubcategories = [
      { id: 1, name: 'Installation Issues', category_id: categoryId },
      { id: 2, name: 'WiFi/Network Connectivity', category_id: categoryId },
      { id: 3, name: 'System Issues', category_id: categoryId },
      { id: 4, name: 'Hardware Problems', category_id: categoryId },
      { id: 5, name: 'Software Issues', category_id: categoryId },
      { id: 6, name: 'Driver Problems', category_id: categoryId },
      { id: 7, name: 'Performance Issues', category_id: categoryId },
      { id: 8, name: 'Configuration Problems', category_id: categoryId },
    ];

    try {
      const res = await api.get(`/subcategories?category_id=${categoryId}`);
      if (res.data?.success) {
        const apiSubs = res.data.data || [];
        // If API returns no subcategories, fall back to device-specific ones
        setSubcategories(apiSubs.length > 0 ? apiSubs : deviceSubcategories);
      } else {
        // If success flag is false, also fall back
        setSubcategories(deviceSubcategories);
      }
    } catch (err) {
      // If API doesn't exist or fails, use hardcoded subcategories
      setSubcategories(deviceSubcategories);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      if (res.success) setUsers(res.data || []);
    } catch (err) {
      // ignore
    }
  };

  const resolveRequesterTenantId = useCallback(() => {
    const rid = parseInt(form.requester_id, 10);
    if (!Number.isNaN(rid)) {
      const row = users.find((x) => Number(x.id) === rid);
      if (row?.tenant_id != null) return row.tenant_id;
    }
    return user?.tenant_id ?? null;
  }, [form.requester_id, users, user?.tenant_id]);

  const effectiveWatchTenantId = useMemo(() => resolveRequesterTenantId(), [resolveRequesterTenantId]);
  const normalizedUserRole = useMemo(() => String(user?.role || '').toLowerCase(), [user?.role]);
  const canChooseAssignmentGroup =
    normalizedUserRole === 'admin' ||
    normalizedUserRole === 'employee' ||
    normalizedUserRole === 'super_admin' ||
    normalizedUserRole === 'global_support';

  // Load tenant admins for watch list (scoped tenant; super_admin filtered by effective tenant)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getUsers('admin');
        if (!res.success || !Array.isArray(res.data)) {
          if (!cancelled) setTenantAdminsWatchUsers([]);
          return;
        }
        let rows = res.data;
        if (normalizedUserRole === 'super_admin') {
          const tid = effectiveWatchTenantId;
          if (tid == null || tid === '') rows = [];
          else rows = rows.filter((u) => Number(u.tenant_id) === Number(tid));
        }
        if (!cancelled) setTenantAdminsWatchUsers(rows);
      } catch {
        if (!cancelled) setTenantAdminsWatchUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [normalizedUserRole, effectiveWatchTenantId]);

  const watchListPickerUsers = useMemo(() => {
    const map = new Map();
    (users || []).forEach((u) => {
      if (u?.id != null) map.set(Number(u.id), u);
    });
    (tenantAdminsWatchUsers || []).forEach((u) => {
      if (u?.id != null) map.set(Number(u.id), u);
    });
    return Array.from(map.values());
  }, [users, tenantAdminsWatchUsers]);

  useEffect(() => {
    if (user?.role !== 'admin') {
      setAssignableAgents([]);
      return;
    }
    const tid = resolveRequesterTenantId();
    if (tid == null || tid === '') {
      setAssignableAgents([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getUsers('employee,admin', { assignmentTenantId: tid });
        if (!cancelled && res.success) {
          setAssignableAgents(res.data || []);
        }
      } catch {
        if (!cancelled) setAssignableAgents([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.role, resolveRequesterTenantId]);

  const fetchAssignmentGroups = async () => {
    try {
      const res = await api.get('/assignment-groups');
      if (res.data?.success && Array.isArray(res.data.data)) {
        const groups = (res.data.data || []).filter((g) =>
          g && (g.is_active === undefined || g.is_active === null || Number(g.is_active) === 1 || g.is_active === true)
        );
        setAssignmentGroups(groups);
      } else {
        setAssignmentGroups([]);
      }
    } catch {
      setAssignmentGroups([]);
    }
  };

  // Clear invalid assignment_group value when assignment groups are loaded
  useEffect(() => {
    if (assignmentGroups.length > 0 && form.assignment_group) {
      const valueStr = form.assignment_group.toString().trim();
      // Check if value is invalid (like "admin0", "undefined", "null", or doesn't match any group)
      if (!valueStr || valueStr === 'undefined' || valueStr === 'null' || valueStr === 'admin0' || 
          !assignmentGroups.some(g => g.id?.toString() === valueStr)) {
        setForm(prev => ({ ...prev, assignment_group: '' }));
      }
    }
  }, [assignmentGroups, form.assignment_group]);

  const handleChange = (key, value) => {
    // Handle subcategory selection first to update UI state appropriately
    if (key === 'subcategory') {
      if (value === 'custom') {
        setShowCustomSubcategory(true);
        setCustomSubcategoryName(''); // Clear any previous custom name
        // Keep subcategory as "custom" to maintain the dropdown selection
        setForm(prev => ({ ...prev, subcategory: 'custom' }));
      } else {
        setShowCustomSubcategory(false);
        setCustomSubcategoryName('');
        // Update subcategory to the selected subcategory ID
        setForm(prev => ({ ...prev, subcategory: value }));
      }
      return; // Early return to avoid the general setForm call below
    }

    // General form update for other fields
    setForm(prev => ({ ...prev, [key]: value }));

    // When category changes, fetch subcategories
    if (key === 'category_id' && value) {
      fetchSubcategories(parseInt(value));
      // Clear subcategory when category changes
      setForm(prev => ({ ...prev, subcategory: '' }));
      setShowCustomSubcategory(false);
      setCustomSubcategoryName('');
    }
  };

  const handleCustomSubcategoryChange = (value) => {
    setCustomSubcategoryName(value);
    // Do NOT update form.subcategory while typing - keep it as "custom"
    // The form.subcategory will remain "custom" to maintain the dropdown selection
  };

  // Handle opening lookup modal
  const openLookupModal = (field, data, title, columns = [], displayKey = 'name') => {
    setLookupModal({
      isOpen: true,
      field,
      data,
      title,
      columns,
      displayKey,
    });
  };

  // Handle selection from lookup modal
  const handleLookupSelect = (item) => {
    if (lookupModal.field === 'caller') {
      // Only admin/employee can change caller; customer field is read-only
      if (user?.role !== 'customer') {
        handleChange('requester_id', item.id.toString());
      }
    } else if (lookupModal.field === 'assignment_group') {
      handleChange('assignment_group', item.id.toString());
    } else if (lookupModal.field === 'assigned_to') {
      handleChange('assigned_to', item.id.toString());
    }
    setLookupModal({ ...lookupModal, isOpen: false });
  };

  // Handle closing lookup modal
  const closeLookupModal = () => {
    setLookupModal({ ...lookupModal, isOpen: false });
  };

  const getPriorityLabel = (p) => {
    const map = {
      '1': 'Critical',
      '2': 'High',
      '3': 'Medium',
      '4': 'Low',
      '5': 'Planning'
    };
    return map[p] || p;
  };

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachmentFiles(files);
  };

  const handleAddToWatchListCreate = (userToAdd) => {
    if (!userToAdd?.id) return;
    setWatchListCreate((prev) => {
      if (prev.some((u) => Number(u.id) === Number(userToAdd.id))) return prev;
      return [...prev, { id: userToAdd.id, name: userToAdd.name, email: userToAdd.email }];
    });
    setShowAddWatchListModal(false);
    setWatchListModalSearch('');
    toast.success('User added to Watch list');
  };

  const handleRemoveFromWatchListCreate = (userId) => {
    setWatchListCreate((prev) => prev.filter((u) => Number(u.id) !== Number(userId)));
    toast.success('User removed from Watch list');
  };

  const canManageWatchListCreate = !watchListLocked;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error("Please fill short description & description");
      return;
    }
    if (!form.requester_id) {
      toast.error("Please select a caller");
      return;
    }

    setLoading(true);
    try {
      // Map UI \"state\" to real backend status enum (OPEN, IN_PROGRESS, RESOLVED, CLOSED)
      // DB default is OPEN, so we only need to override when not \"new\".
      let statusForCreate = undefined; // let backend default to OPEN
      const uiState = String(form.state || '').toLowerCase();
      if (uiState === 'in_progress' || uiState === 'pending') {
        statusForCreate = 'IN_PROGRESS';
      } else if (uiState === 'resolved') {
        statusForCreate = 'RESOLVED';
      } else if (uiState === 'closed') {
        statusForCreate = 'CLOSED';
      }

      const res = await createTicket({
        subject: form.subject,
        description: form.description,
        category_id: form.category_id ? parseInt(form.category_id, 10) : null,
        subcategory_id: form.subcategory === 'custom' ? null : (form.subcategory && form.subcategory.trim() && !isNaN(parseInt(form.subcategory, 10)) ? parseInt(form.subcategory, 10) : null),
        subcategory_name: form.subcategory === 'custom' && customSubcategoryName.trim() ? customSubcategoryName.trim() : null,
        priority: form.priority,
        assignment_group_id: form.assignment_group && form.assignment_group.trim() ? parseInt(form.assignment_group, 10) : null,
        assigned_to:
          user?.role === "admin" && form.assigned_to && form.assigned_to.trim()
            ? parseInt(form.assigned_to, 10)
            : null,
        requester_id: form.requester_id && form.requester_id.trim() ? parseInt(form.requester_id, 10) : null,
        additional_comments: form.additional_comments || "",
        work_notes: form.work_notes || "",
        location: form.location || "",
        // Only send status when we explicitly want something other than default OPEN
        ...(statusForCreate ? { status: statusForCreate } : {}),
      });

      if (res.success) {
        // Extract ticket number and ID from response
        let finalTicketNumber = res.data?.ticket_number || 
                                  res.data?.ticketNumber || 
                                  res.data?.data?.ticket_number ||
                                  res.data?.data?.ticketNumber;
        const ticketId = res.data?.id || res.data?.data?.id;

        // Extract only numeric part from formats like "TKT_1001_2026" or "TKT-1001-2026"
        if (finalTicketNumber && typeof finalTicketNumber === 'string') {
          const numericMatch = String(finalTicketNumber).match(/\d+/);
          if (numericMatch) {
            finalTicketNumber = numericMatch[0];
          }
        }

        // Set ticket number in state (updates Number field)
        if (finalTicketNumber) {
          setTicketNumber(String(finalTicketNumber));
        }

        // Upload attachments if any were selected
        if (attachmentFiles.length && ticketId) {
          for (const file of attachmentFiles) {
            try {
              const uploadRes = await uploadAttachment(ticketId, file);
              if (!uploadRes.success) {
                toast.error(`Failed to upload ${file.name}`);
              }
            } catch (uploadError) {
              console.error('Attachment upload error:', uploadError);
              toast.error(`Failed to upload ${file.name}`);
            }
          }
          setAttachmentFiles([]);
        }

        // Add Watch list users (followers) to the new ticket
        if (ticketId && watchListCreate.length > 0) {
          try {
            await Promise.all(
              watchListCreate.map((member) => addFollower(ticketId, member.id))
            );
          } catch (watchErr) {
            console.error('Watch list add error:', watchErr);
            toast.error('Ticket created but some Watch list users could not be added.');
          }
        }

        // Show success toast with ticket number
        if (finalTicketNumber) {
          toast.success("Ticket created successfully", { 
            description: `Ticket #${finalTicketNumber} has been created` 
          });
        } else {
          toast.success("Ticket created successfully");
        }

        // Delay redirect so user can see the ticket number
        setTimeout(() => {
          if (ticketId) {
            navigate(`/tickets/${ticketId}`);
          } else {
            navigate(
              user?.role === "admin" || user?.role === "employee"
                ? "/admin/dashboard"
                : "/customer/dashboard"
            );
          }
        }, 800); // 800ms delay - enough to see the number
      }
    } catch (err) {
      toast.error("Failed to create ticket");
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  };

  const openedDisplay = openedAt.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).replace(',', '');

  return (
    <DashboardLayout userRole={user?.role || 'customer'}>
      {/* MAIN CONTENT - same mobile top bar + drawer as Dashboard */}
      <div className="flex flex-col overflow-hidden min-h-full">
        {/* COMMAND HEADER - Premium Control Bar */}
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-200/70 dark:border-slate-700/70 px-4 py-3 flex justify-between items-center shadow-sm shadow-indigo-500/5">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(user?.role === 'admin' ? '/admin/dashboard' : '/customer/dashboard')}
              className="p-2 rounded-xl hover:bg-indigo-50/60 dark:hover:bg-indigo-900/30 transition-all duration-200 hover:scale-105 hover:shadow-sm hover:shadow-indigo-500/30"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-200" />
            </button>
            <div className="relative">
              {/* Soft glow behind title */}
              <div className="pointer-events-none absolute -top-4 -left-6 h-16 w-44 rounded-full bg-indigo-500/10 blur-2xl" />

              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">
                Create New Ticket
              </h1>

              {/* Thin gradient underline */}
              <div className="mt-2 h-1 w-28 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600" />

              <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                Fill in the details to create a support ticket
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="unstyled"
              onClick={() => navigate(-1)}
              className="inline-flex items-center justify-center gap-2 h-10 px-5 text-sm font-semibold rounded-xl
                bg-gradient-to-r from-red-600 via-red-600 to-rose-600
                text-white
                shadow-md shadow-red-500/30
                hover:shadow-xl hover:shadow-red-500/40
                hover:-translate-y-0.5 hover:scale-[1.03]
                active:scale-95
                transition-all duration-300 ease-out
                whitespace-nowrap"
            >
              Cancel
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={loading}
              variant="unstyled"
              className="inline-flex items-center justify-center gap-2 h-10 px-5 text-sm font-semibold rounded-xl
                bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600
                text-white
                shadow-md shadow-indigo-500/40
                hover:shadow-xl hover:shadow-indigo-500/50
                hover:-translate-y-0.5 hover:scale-[1.03]
                active:scale-95
                transition-all duration-300 ease-out
                disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:-translate-y-0
                whitespace-nowrap"
            >
              {loading ? 'Submitting…' : 'Submit'}
            </Button>
          </div>
        </div>

        {/* MAIN CONTENT AREA */}
        <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50 via-gray-50/70 to-indigo-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950/30 p-6">
          <div className="w-full max-w-none">
            {/* PRIMARY FORM SURFACE */}
            <div className="relative bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 dark:border-slate-700/60 overflow-hidden mb-6 shadow-lg shadow-indigo-500/5">
              <div className="p-4 md:p-5">
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Two Column Layout */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                  {/* LEFT COLUMN */}
                  <div className="space-y-3">
                    {/* Number */}
                    <div className="grid grid-cols-4 items-center gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="number">
                        Number
                      </label>
                      <div className="col-span-3">
                        <div className="relative">
                          <input
                            className="w-full bg-gray-100/80 dark:bg-slate-800/60 border border-dashed border-gray-300 dark:border-slate-600 rounded-xl text-gray-500 dark:text-gray-400 px-4 py-2.5 pr-10 text-sm font-mono font-medium cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500/60"
                            id="number"
                            readOnly
                            type="text"
                            value={loadingNumber ? 'Loading preview...' : (ticketNumber || '')}
                            placeholder={loadingNumber ? 'Loading preview...' : (ticketNumber ? '' : 'Will be generated on submit')}
                            title={ticketNumber ? `Preview ticket number: ${ticketNumber}` : (loadingNumber ? "Loading ticket number preview..." : "Ticket number will be generated when you submit the form")}
                          />
                          {loadingNumber && (
                            <div className="absolute right-3 top-2.5">
                              <svg className="animate-spin h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Caller */}
                    <div className="grid grid-cols-4 items-center gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="caller">
                        Caller <span className="ml-1 text-indigo-500">*</span>
                      </label>
                      <div className="col-span-3">
                        <ReferenceField
                          value={form.requester_id}
                          onChange={val => handleChange('requester_id', val)}
                          items={users}
                          placeholder="Search..."
                          readOnly={user?.role === 'customer'}
                          showLock={true}
                          showInfo={false}
                          showSearch={true}
                          infoTooltip="Select the person who reported this incident"
                          fieldName="Caller"
                          onSearchClick={() => openLookupModal('caller', users, 'Select Caller', [
                            { key: 'name', label: 'Name', width: '30%' },
                            { key: 'email', label: 'Email', width: '30%' },
                            { key: 'mobile_phone', label: 'Phone', width: '20%' },
                            { key: 'job_title', label: 'Title', width: '20%' }
                          ], 'name')}
                        />
                      </div>
                    </div>

                    {/* Location - Suggestion input (no custom dropdown) */}
                    <div className="grid grid-cols-4 items-center gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="location">
                        Location
                      </label>
                      <div className="col-span-3 relative">
                        <div className="relative">
                          <input
                            type="text"
                            id="location"
                            value={form.location}
                            onChange={(e) => handleChange('location', e.target.value)}
                            placeholder="Type city or state in India..."
                            className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 pr-10"
                            autoComplete="off"
                          />
                          <div className="absolute right-3 top-2.5 text-gray-400 dark:text-gray-500 pointer-events-none">
                            <MapPin className="w-4 h-4" />
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {locationSuggestions.slice(0, 8).map((item) => (
                            <button
                              key={item}
                              type="button"
                              onClick={() => handleChange('location', item)}
                              className="px-2.5 py-1 text-xs rounded-full border border-indigo-200 dark:border-indigo-700/60 text-indigo-700 dark:text-indigo-300 bg-indigo-50/70 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/35 transition-colors"
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Category */}
                    <div className="grid grid-cols-4 items-center gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="category">
                        Category
                      </label>
                      <div className="col-span-3">
                        <select
                          className="w-full border border-dashed border-gray-300 dark:border-slate-600 rounded-xl bg-gray-100/80 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400 px-4 py-2.5 text-sm cursor-not-allowed"
                          id="category"
                          value={form.category_id}
                          disabled
                          title="Category is set to Technical Support for electronic device issues"
                        >
                          {categories.length > 0 ? (
                            categories.map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))
                          ) : (
                            <option value="">Technical Support</option>
                          )}
                        </select>
                      </div>
                    </div>

                    {/* Subcategory */}
                    <div className="grid grid-cols-4 items-center gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="subcategory">
                        Subcategory
                      </label>
                      <div className="col-span-3 space-y-2">
                        {!showCustomSubcategory ? (
                          <select
                            className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                            id="subcategory"
                            value={form.subcategory}
                            onChange={e => handleChange('subcategory', e.target.value)}
                          >
                            <option value="">-- None --</option>
                            {subcategories.map(sub => (
                              <option key={sub.id} value={sub.id}>{sub.name}</option>
                            ))}
                            <option value="custom">+ Add Custom Subcategory</option>
                          </select>
                        ) : (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              className="flex-1 rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                              placeholder="Enter custom subcategory name"
                              value={customSubcategoryName}
                              onChange={e => handleCustomSubcategoryChange(e.target.value)}
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setShowCustomSubcategory(false);
                                setCustomSubcategoryName('');
                                setForm(prev => ({ ...prev, subcategory: '' })); // Reset subcategory to empty on cancel
                              }}
                              className="px-4 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-all duration-200"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Impact */}
                    <div className="grid grid-cols-4 items-center gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="impact">
                        Impact
                      </label>
                      <div className="col-span-3">
                        <select
                          className="w-full border border-gray-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-200 px-4 py-2.5 text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-400"
                          id="impact"
                          value={form.impact}
                          onChange={e => handleChange('impact', e.target.value)}
                        >
                          <option value="1">1 - Critical</option>
                          <option value="2">2 - High</option>
                          <option value="3">3 - Medium</option>
                          <option value="4">4 - Low</option>
                        </select>
                      </div>
                    </div>

                    {/* Urgency */}
                    <div className="grid grid-cols-4 items-center gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="urgency">
                        Urgency
                      </label>
                      <div className="col-span-3">
                        <select
                          className="w-full border border-gray-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-200 px-4 py-2.5 text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-400"
                          id="urgency"
                          value={form.urgency}
                          onChange={e => handleChange('urgency', e.target.value)}
                        >
                          <option value="1">1 - Critical</option>
                          <option value="2">2 - High</option>
                          <option value="3">3 - Medium</option>
                          <option value="4">4 - Low</option>
                        </select>
                      </div>
                    </div>

                    {/* Priority */}
                    <div className="grid grid-cols-4 items-center gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="priority">
                        Priority
                      </label>
                      <div className="col-span-3">
                        <select
                          className="w-full border border-dashed border-gray-300 dark:border-slate-600 rounded-xl bg-gray-100/80 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400 px-4 py-2.5 text-sm cursor-not-allowed"
                          id="priority"
                          value={form.priority}
                          disabled
                        >
                          <option value={form.priority}>{form.priority} - {getPriorityLabel(form.priority)}</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT COLUMN */}
                  <div className="space-y-3">
                    {/* Opened */}
                    <div className="grid grid-cols-4 items-center gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="opened">
                        Opened
                      </label>
                      <div className="col-span-3 relative">
                        <input
                          className="w-full border border-dashed border-gray-300 dark:border-slate-600 rounded-xl bg-gray-100/80 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400 pr-8 px-4 py-2.5 text-sm font-mono cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500/60"
                          id="opened"
                          type="text"
                          value={openedDisplay}
                          readOnly
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <Tooltip content="Select date and time">
                            <button
                              type="button"
                              className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200"
                            >
                              <Calendar className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    </div>

                    {/* Opened by */}
                    <div className="grid grid-cols-4 items-center gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="opened-by">
                        Opened by
                      </label>
                      <div className="col-span-3 relative">
                        <input
                          className="w-full border border-dashed border-gray-300 dark:border-slate-600 rounded-xl bg-gray-100/80 dark:bg-slate-800/60 text-gray-500 dark:text-gray-400 px-4 py-2.5 text-sm cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500/60"
                          id="opened-by"
                          type="text"
                          value={(user?.name || 'System Administrator').replace(/0+$/, '').trim()}
                          readOnly
                        />
                      </div>
                    </div>

                    {/* Contact type */}
                    <div className="grid grid-cols-4 items-center gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="contact-type">
                        Contact type
                      </label>
                      <div className="col-span-3">
                        <select
                          className="w-full border border-gray-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-200 px-4 py-2.5 text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-400"
                          id="contact-type"
                          value={form.contact_type}
                          onChange={e => handleChange('contact_type', e.target.value)}
                        >
                          <option value="phone">Phone</option>
                          <option value="email">Email</option>
                          <option value="self_service">Self-service</option>
                        </select>
                      </div>
                    </div>

                    {/* State (UI label only) – we map this to real DB statuses when creating the ticket */}
                    <div className="grid grid-cols-4 items-center gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="state">
                        State
                      </label>
                      <div className="col-span-3">
                        <select
                          className="w-full border border-gray-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-200 px-4 py-2.5 text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-400"
                          id="state"
                          value={form.state}
                          onChange={e => handleChange('state', e.target.value)}
                        >
                          <option value="new">New</option>
                          <option value="in_progress">Active</option>
                          <option value="pending">Pending</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                    </div>

                    {/* Assignment group - visible for internal staff only */}
                    {canChooseAssignmentGroup && (
                      <div className="grid grid-cols-4 items-center gap-3">
                        <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="assignment-group">
                          Assignment group
                        </label>
                        <div className="col-span-3">
                          <ReferenceField
                            value={form.assignment_group}
                            onChange={val => handleChange('assignment_group', val)}
                            items={assignmentGroups}
                            placeholder="Search by name, description, or email..."
                            displayKey="name"
                            disabled={false}
                            showLock={false}
                            showInfo={false}
                            showSearch={true}
                            infoTooltip="Select the team that should handle this ticket. Search by name, description, or email address."
                            fieldName="Assignment group"
                            showDetails={true}
                            searchFields={['description', 'email']}
                            onSearchClick={() => openLookupModal('assignment_group', assignmentGroups, 'Select Assignment Group', [
                              { key: 'name', label: 'Name', width: '40%' },
                              { key: 'description', label: 'Description', width: '40%' },
                              { key: 'email', label: 'Email', width: '20%' }
                            ], 'name')}
                          />
                        </div>
                      </div>
                    )}

                    {/* Assigned to - Only visible to admin (not employees or customers) */}
                    {user?.role === 'admin' && (
                      <div className="grid grid-cols-4 items-center gap-3">
                        <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 col-span-1" htmlFor="assigned-to">
                          Assigned to
                        </label>
                        <div className="col-span-3">
                          <ReferenceField
                            value={form.assigned_to}
                            onChange={val => handleChange('assigned_to', val)}
                            items={assignableAgents}
                            placeholder="Search..."
                            disabled={false}
                            showLock={false}
                            showInfo={false}
                            showSearch={true}
                            infoTooltip="Assign this incident to a specific user (Admin only)"
                            fieldName="Assigned to"
                            onSearchClick={() => openLookupModal('assigned_to', assignableAgents, 'Select Assigned To', [
                              { key: 'name', label: 'Name', width: '30%' },
                              { key: 'email', label: 'Email', width: '30%' },
                              { key: 'mobile_phone', label: 'Phone', width: '20%' },
                              { key: 'job_title', label: 'Title', width: '20%' }
                            ], 'name')}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  </div>

                  {/* SHORT DESCRIPTION - Full width */}
                  <div className="md:col-span-2 pt-4 mt-2 border-t border-dashed border-gray-300/60 dark:border-slate-700/60">
                    <div className="grid grid-cols-1 md:grid-cols-8 items-start gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 pt-2 col-span-1 whitespace-nowrap" htmlFor="short-description">
                        Short description <span className="ml-1 text-indigo-500">*</span>
                      </label>
                      <div className="col-span-1 md:col-span-7 relative">
                        <input
                          className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 pr-8"
                          id="short-description"
                          type="text"
                          value={form.subject}
                          onChange={e => handleChange('subject', e.target.value)}
                          placeholder="Enter a brief description of the issue"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <Tooltip content={form.subject && form.subject.trim().length >= 3 ? "Search Knowledge Base for solutions" : "Enter at least 3 characters to search KB"}>
                            <button
                              type="button"
                              className={`p-2 rounded-xl border transition-all duration-200 ${
                                form.subject && form.subject.trim().length >= 3
                                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 hover:text-amber-700 dark:hover:text-amber-300 animate-pulse'
                                  : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:text-amber-700 dark:hover:text-amber-300'
                              }`}
                              onClick={() => {
                                if (form.subject && form.subject.trim().length >= 3) {
                                  setKbModalOpen(true);
                                } else {
                                  toast.info('Enter at least 3 characters in the short description to search Knowledge Base');
                                }
                              }}
                            >
                              <Lightbulb className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* DESCRIPTION - Full width */}
                  <div className="md:col-span-2">
                    <div className="grid grid-cols-1 md:grid-cols-8 items-start gap-3">
                      <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 pt-2 col-span-1 whitespace-nowrap" htmlFor="description">
                        Description <span className="ml-1 text-indigo-500">*</span>
                      </label>
                      <div className="col-span-1 md:col-span-7">
                        <textarea
                          className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none"
                          id="description"
                          rows={3}
                          value={form.description}
                          onChange={e => handleChange('description', e.target.value)}
                          placeholder="Enter detailed description of the issue, steps to reproduce, error messages, etc."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Related Search Results */}
                  {form.subject && form.subject.trim().length >= 3 && (
                    <div className="md:col-span-2">
                      <div className="grid grid-cols-1 md:grid-cols-8">
                        <div className="col-span-1"></div>
                        <div className="col-span-1 md:col-span-7">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:underline transition-colors group"
                            onClick={() => setKbModalOpen(true)}
                          >
                            <Lightbulb className="w-4 h-4 text-amber-500" />
                            <span>Search Knowledge Base for solutions</span>
                            <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* SECONDARY PANELS - Notes & Activity */}
            <div className="relative bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 dark:border-slate-700/60 overflow-hidden shadow-lg shadow-indigo-500/5">
              <div className="px-4 py-3 border-b border-dashed border-gray-300/60 dark:border-slate-700/60 bg-gradient-to-r from-gray-50 to-indigo-50/40 dark:from-slate-900 dark:to-indigo-950/30">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Notes & Activity</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Add comments and track changes</p>
                  </div>
                  <button
                    type="button"
                    className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200"
                    onClick={() => setNotesExpanded(!notesExpanded)}
                  >
                    {notesExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                </div>

                {notesExpanded && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mb-4">
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-32 flex-shrink-0">Watch list</span>
                          <div className="flex-grow flex items-center space-x-2">
                            <Tooltip content={watchListLocked ? 'Unlock watch list' : 'Lock watch list'}>
                              <button
                                type="button"
                                className={`p-2.5 rounded-xl border transition-all duration-200 ${watchListLocked
                                    ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                                    : 'border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 text-gray-500 dark:text-gray-400'
                                  }`}
                                onClick={() => {
                                  setWatchListLocked(!watchListLocked);
                                  toast.info(watchListLocked ? 'Watch list unlocked' : 'Watch list locked');
                                }}
                              >
                                <Lock className="w-5 h-5" />
                              </button>
                            </Tooltip>
                            {canManageWatchListCreate && (
                              <Tooltip content="Add user to Watch list">
                                <button
                                  type="button"
                                  className="p-2.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 text-gray-500 dark:text-gray-400 transition-all duration-200"
                                  onClick={() => {
                                    setWatchListModalSearch('');
                                    setShowAddWatchListModal(true);
                                  }}
                                >
                                  <UserPlus className="w-5 h-5" />
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                        <div className="ml-0 md:ml-32">
                          {watchListCreate.length === 0 ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400">No users on Watch list. Add users to notify them when this ticket is created.</p>
                          ) : (
                            <ul className="space-y-2 max-h-44 overflow-y-auto overscroll-contain pr-1 [scrollbar-width:thin] [scrollbar-color:rgb(203_213_225)_transparent] dark:[scrollbar-color:rgb(71_85_105)_transparent] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300/90 dark:[&::-webkit-scrollbar-thumb]:bg-slate-600">
                              {watchListCreate.map((member) => (
                                <li key={member.id} className="flex items-center justify-between gap-2 py-1.5 px-3 rounded-lg bg-gray-50 dark:bg-slate-800/60 border border-gray-100 dark:border-slate-700">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{cleanUserName(member.name) || member.email || 'Unknown'}</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate hidden sm:inline">({member.email})</span>
                                  </div>
                                  {canManageWatchListCreate && (
                                    <Tooltip content="Remove from Watch list">
                                      <button
                                        type="button"
                                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                        onClick={() => handleRemoveFromWatchListCreate(member.id)}
                                      >
                                        <UserMinus className="w-4 h-4" />
                                      </button>
                                    </Tooltip>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {/* Attachments */}
                      <div>
                        <label className="block text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 mb-1">
                          Attachments
                        </label>
                        <input
                          type="file"
                          multiple
                          onChange={handleAttachmentChange}
                          className="block w-full text-sm text-gray-600 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 dark:file:bg-indigo-900/40 dark:file:text-indigo-200 cursor-pointer"
                        />
                        {attachmentFiles.length > 0 && (
                          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                            {attachmentFiles.length} file{attachmentFiles.length > 1 ? 's' : ''} selected
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 mb-1" htmlFor="additional-comments">
                          Additional comments <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(Customer visible)</span>
                        </label>
                        <textarea
                          className="w-full rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none"
                          id="additional-comments"
                          rows={3}
                          value={form.additional_comments}
                          onChange={e => handleChange('additional_comments', e.target.value)}
                          placeholder="Comments visible to the customer"
                        />
                      </div>
                    </div>

                    {/* ACTIVITY SECTION */}
                    <div className="mt-6 pt-6 border-t border-dashed border-gray-300/60 dark:border-slate-700/60">
                      <div className="flex items-start gap-4">
                        <label className="text-xs md:text-sm font-semibold tracking-wide text-gray-600 dark:text-gray-300 w-28 flex-shrink-0 pt-2" htmlFor="activity">
                          Activity
                        </label>
                        <div className="flex-1 min-w-0">
                          {/* Header: count + icons */}
                          <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {showActivitySample ? '1 item (sample)' : '0 items'}
                            </span>
                            <div className="flex items-center gap-1">
                              <Tooltip content="Remove sample activity">
                                <button
                                  type="button"
                                  onClick={() => setShowActivitySample(false)}
                                  className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </Tooltip>
                              <Tooltip content="Filter available on ticket detail after creation">
                                <span className="inline-block">
                                  <button
                                    type="button"
                                    disabled
                                    className="p-2 rounded-xl bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                  >
                                    <Filter className="w-4 h-4" />
                                  </button>
                                </span>
                              </Tooltip>
                              <Tooltip content={activityExpanded ? 'Collapse activity' : 'Expand activity'}>
                                <button
                                  type="button"
                                  onClick={() => setActivityExpanded(!activityExpanded)}
                                  className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200"
                                >
                                  {activityExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </button>
                              </Tooltip>
                            </div>
                          </div>
                          {activityExpanded && (
                            <>
                              {showActivitySample ? (
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  <span className="font-medium text-gray-700 dark:text-gray-300">{openedDisplay}</span>
                                  {' - '}
                                  <span className="font-semibold text-gray-900 dark:text-gray-100">{cleanUserName(user?.name || 'System Administrator')}</span>
                                  <span className="text-gray-500 dark:text-gray-500 text-xs ml-2">
                                    Changed: Assigned to, Additional comments, Impact, Incident state, Opened by, Priority
                                  </span>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No activity yet. Activity will appear here after the ticket is created.</p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Knowledge Base Modal */}
      <KBSuggestionsModal
        isOpen={kbModalOpen}
        onClose={() => setKbModalOpen(false)}
        searchTerm={form.subject}
        onSelectSuggestion={(article) => {
          // Auto-fill description with KB article content when user selects an article
          if (article.content || article.title) {
            const content = article.content
              ? (article.content.length > 800 ? article.content.substring(0, 800) + '...' : article.content)
              : '';
            const newDescription = content
              ? `Reference: ${article.title}\n\n${content}`
              : `Reference: ${article.title}`;
            setForm(prev => ({ ...prev, description: newDescription }));
            toast.success('KB article content added to description');
          }
        }}
      />

      {/* Reference Lookup Modal */}
      <ReferenceLookupModal
        isOpen={lookupModal.isOpen}
        onClose={closeLookupModal}
        onSelect={handleLookupSelect}
        title={lookupModal.title}
        data={lookupModal.data}
        columns={lookupModal.columns}
        displayKey={lookupModal.displayKey}
      />

      {/* Add to Watch list modal (Create Ticket) */}
      {showAddWatchListModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => {
            setShowAddWatchListModal(false);
            setWatchListModalSearch('');
          }}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full flex flex-col max-h-[85vh] overflow-hidden border border-gray-200 dark:border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add to Watch list</h3>
              <button
                type="button"
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
                onClick={() => {
                  setShowAddWatchListModal(false);
                  setWatchListModalSearch('');
                }}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="px-4 pt-3 pb-2 border-b border-gray-200 dark:border-slate-700 shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                <input
                  type="search"
                  autoComplete="off"
                  placeholder="Search by name or email..."
                  value={watchListModalSearch}
                  onChange={(e) => setWatchListModalSearch(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                />
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4 overscroll-contain [scrollbar-width:thin] [scrollbar-color:rgb(203_213_225)_transparent] dark:[scrollbar-color:rgb(71_85_105)_transparent] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300/90 dark:[&::-webkit-scrollbar-thumb]:bg-slate-600">
              {(() => {
                const watchListIds = new Set((watchListCreate || []).map((w) => Number(w.id)));
                let available = (watchListPickerUsers || []).filter((u) => !watchListIds.has(Number(u.id)));
                const q = watchListModalSearch.trim().toLowerCase();
                if (q) {
                  available = available.filter((u) => {
                    const name = (cleanUserName(u.name || '') || '').toLowerCase();
                    const email = (u.email || '').toLowerCase();
                    return name.includes(q) || email.includes(q);
                  });
                }
                if (available.length === 0) {
                  return (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {q ? 'No users match your search.' : 'All users are already on the Watch list.'}
                    </p>
                  );
                }
                return (
                  <ul className="space-y-1">
                    {available.map((u) => (
                      <li key={u.id}>
                        <button
                          type="button"
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800 transition-colors"
                          onClick={() => handleAddToWatchListCreate(u)}
                        >
                          <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 block truncate">{cleanUserName(u.name || '') || u.email || 'User'}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 truncate block">{u.email}</span>
                          </div>
                          <UserPlus className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}